import { useCallback, useEffect, useRef, useState } from 'react';

/** How wide the left column opens at, before anybody has dragged the handle. */
const DEFAULT_WIDTH = 448;

/** Neither column may be dragged narrower than this. */
const MIN_LEFT = 280;
const MIN_RIGHT = 320;

/** How far one arrow key moves the handle. */
const KEYBOARD_STEP = 32;

function clamp(width: number, containerWidth: number): number {
  // A container narrower than both minimums cannot honour them; the left column
  // gives way first, since the list on the right is the one being read. One
  // that has not been laid out yet bounds nothing on the right — it has no
  // width to bound with — but its minimum still holds.
  const max =
    containerWidth > 0 ? Math.max(MIN_LEFT, containerWidth - MIN_RIGHT) : Number.POSITIVE_INFINITY;

  return Math.min(max, Math.max(MIN_LEFT, width));
}

export interface ResizableSplit {
  /** Put on the grid whose first column is being sized. */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Current width of the left column, in pixels. */
  width: number;
  /** Spread onto the drag handle between the two columns. */
  separatorProps: {
    role: 'separator';
    tabIndex: 0;
    'aria-orientation': 'vertical';
    'aria-valuenow': number;
    'aria-label': string;
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
    onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
    onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
    onDoubleClick: () => void;
    'data-dragging': boolean;
  };
  /** Whether the handle is being dragged right now, for the page's own cursor. */
  dragging: boolean;
}

/**
 * A draggable split between two columns, remembered across visits.
 *
 * The board dashboard is a photo beside a list, and how much of the window each
 * deserves is not something the layout can know: a wall with a dense grid on it
 * is unreadable at 26rem, while somebody scanning a few hundred problems wants
 * the list. So the reader sets it, and the panel keeps their answer where it
 * keeps the sidebar's — a preference of theirs, not of this board.
 *
 * Double-clicking the handle puts it back. Arrow keys move it too: the handle is
 * a real separator, and dragging is not something every reader can do.
 */
export function useResizableSplit(storageKey: string, label: string): ResizableSplit {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [dragging, setDragging] = useState(false);
  const [width, setWidth] = useState(() => {
    const stored = Number(localStorage.getItem(storageKey));
    return Number.isFinite(stored) && stored > 0 ? stored : DEFAULT_WIDTH;
  });

  // Written on every change rather than on drop: a keyboard nudge and a
  // double-click are both changes nobody drops.
  useEffect(() => {
    localStorage.setItem(storageKey, String(width));
  }, [storageKey, width]);

  const resize = useCallback((next: number) => {
    setWidth(clamp(next, containerRef.current?.getBoundingClientRect().width ?? 0));
  }, []);

  const onPointerDown = useCallback((event: React.PointerEvent<HTMLElement>) => {
    // The pointer has to stay with the handle even when the drag outruns it,
    // which a 20px-wide target does within one flick.
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.preventDefault();
    setDragging(true);
  }, []);

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (!dragging) return;
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      resize(event.clientX - rect.left);
    },
    [dragging, resize]
  );

  const onPointerUp = useCallback((event: React.PointerEvent<HTMLElement>) => {
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    setDragging(false);
  }, []);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      resize(width + (event.key === 'ArrowRight' ? KEYBOARD_STEP : -KEYBOARD_STEP));
    },
    [resize, width]
  );

  const reset = useCallback(() => resize(DEFAULT_WIDTH), [resize]);

  return {
    containerRef,
    width,
    dragging,
    separatorProps: {
      role: 'separator',
      tabIndex: 0,
      'aria-orientation': 'vertical',
      'aria-valuenow': Math.round(width),
      'aria-label': label,
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onKeyDown,
      onDoubleClick: reset,
      'data-dragging': dragging,
    },
  };
}
